import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Tv, Mail, BarChart3, X, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { formatDate } from '@/utils/cn';

export function PartnerClientsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const startImpersonation = useAuthStore(s => s.startImpersonation);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);

  const { data } = useQuery({
    queryKey: ['partner-clients', search],
    queryFn: () => apiClient.partner.listClients({ search: search || undefined, limit: 100 }).then(r => r.data),
  });

  const resendMut = useMutation({
    mutationFn: (id: number) => apiClient.partner.resendClientInvite(id),
    onSuccess: () => toast.success('Convite reenviado por e-mail.'),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Falha ao reenviar convite.'),
  });
  const impersonateMut = useMutation({
    mutationFn: (id: number) => apiClient.partner.impersonateClient(id).then(r => r.data),
    onSuccess: (d: any) => {
      startImpersonation(d.access_token, d.expires_at);
      toast.success(`Entrando como ${d.tenant_name}…`);
      navigate('/dashboard');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Não foi possível entrar como este cliente.'),
  });

  return (
    <div className="container-app py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Meus clientes</h1>
          <p className="text-muted-foreground">Contas que você cadastrou no MyShow</p>
        </div>
        <Button onClick={() => setShowNew(true)}>
          <Plus className="h-4 w-4 mr-1" /> Novo cliente
        </Button>
      </div>

      <div className="relative max-w-md mt-6 mb-6">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar (empresa, subdomínio)..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="p-3 text-xs uppercase">Empresa</th>
              <th className="p-3 text-xs uppercase">Contato</th>
              <th className="p-3 text-xs uppercase">Status</th>
              <th className="p-3 text-xs uppercase">TVs</th>
              <th className="p-3 text-xs uppercase">Trial até</th>
              <th className="p-3 text-xs uppercase">Último acesso</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((c: any) => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30">
                <td className="p-3">
                  <div className="font-medium">{c.nome_fantasia || c.razao_social}</div>
                  <div className="text-xs text-muted-foreground font-mono">{c.subdomain}</div>
                </td>
                <td className="p-3">
                  <div>{c.contact_name || '—'}</div>
                  <div className="text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
                </td>
                <td className="p-3">
                  <Badge variant={c.status === 'active' ? 'success' : c.status === 'trial' ? 'default' : 'muted'}>
                    {c.status}
                  </Badge>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 text-xs ${c.tvs_online > 0 ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>
                    <Tv className="h-3.5 w-3.5" />{c.tvs_online}/{c.tvs_total}
                  </span>
                </td>
                <td className="p-3">{formatDate(c.trial_ends_at)}</td>
                <td className="p-3">{c.last_user_login_at ? formatDate(c.last_user_login_at) : <span className="text-muted-foreground">nunca</span>}</td>
                <td className="p-3 flex gap-1 flex-wrap">
                  <Link to={`/parceiro/clientes/${c.id}`}>
                    <Button size="sm" variant="ghost" title="Detalhes">
                      <BarChart3 className="h-3.5 w-3.5 mr-1" /> Detalhes
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" title="Reenviar convite de acesso" disabled={resendMut.isPending} onClick={() => resendMut.mutate(c.id)}>
                    <Mail className="h-3.5 w-3.5 mr-1" /> Convite
                  </Button>
                  <Button size="sm" variant="ghost" className="text-brand-600" title="Entrar como este cliente" disabled={impersonateMut.isPending} onClick={() => impersonateMut.mutate(c.id)}>
                    <LogIn className="h-3.5 w-3.5 mr-1" /> Entrar
                  </Button>
                </td>
              </tr>
            ))}
            {data?.items?.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhum cliente ainda. Clique em “Novo cliente” pra começar.</td></tr>
            )}
          </tbody>
        </table>
        <div className="p-3 text-xs text-muted-foreground border-t border-border">{data?.total ?? 0} cliente(s)</div>
      </Card>

      {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ['partner-clients'] }); qc.invalidateQueries({ queryKey: ['partner-me'] }); }} />}
    </div>
  );
}

function NewClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    razaoSocial: '', nomeFantasia: '', cnpj: '', contactName: '',
    emailPrimary: '', phone: '', segment: '', subdomain: '',
    addressCidade: '', addressEstado: '',
  });

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.razaoSocial.trim() && form.contactName.trim() && /\S+@\S+\.\S+/.test(form.emailPrimary);

  const createMut = useMutation({
    mutationFn: () => apiClient.partner.createClient({
      razaoSocial: form.razaoSocial.trim(),
      nomeFantasia: form.nomeFantasia.trim() || undefined,
      cnpj: form.cnpj.trim() || undefined,
      contactName: form.contactName.trim(),
      emailPrimary: form.emailPrimary.trim(),
      phone: form.phone.trim() || undefined,
      segment: form.segment.trim() || undefined,
      subdomain: form.subdomain.trim() || undefined,
      addressCidade: form.addressCidade.trim() || undefined,
      addressEstado: form.addressEstado.trim() || undefined,
    }),
    onSuccess: () => {
      toast.success('Cliente cadastrado! Enviamos um convite por e-mail pra ele definir a senha.');
      onCreated();
      onClose();
    },
    onError: (e: any) => {
      const code = e?.response?.data?.error;
      if (code === 'email_taken') toast.error('Já existe uma conta com este e-mail.');
      else if (code === 'cnpj_taken') toast.error('Já existe uma conta com este CNPJ.');
      else toast.error(e?.response?.data?.message ?? 'Falha ao cadastrar cliente.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white">
          <h2 className="text-lg font-bold">Novo cliente</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Razão social *</label>
            <Input value={form.razaoSocial} onChange={e => set('razaoSocial', e.target.value)} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Nome fantasia</label>
              <Input value={form.nomeFantasia} onChange={e => set('nomeFantasia', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">CNPJ</label>
              <Input value={form.cnpj} onChange={e => set('cnpj', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Nome do contato *</label>
            <Input value={form.contactName} onChange={e => set('contactName', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">E-mail *</label>
              <Input type="email" value={form.emailPrimary} onChange={e => set('emailPrimary', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Telefone / WhatsApp</label>
              <Input value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Segmento</label>
              <Input placeholder="mercado, farmácia, padaria..." value={form.segment} onChange={e => set('segment', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Subdomínio (opcional)</label>
              <Input placeholder="auto se vazio" value={form.subdomain} onChange={e => set('subdomain', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Cidade</label>
              <Input value={form.addressCidade} onChange={e => set('addressCidade', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">UF</label>
              <Input maxLength={2} value={form.addressEstado} onChange={e => set('addressEstado', e.target.value.toUpperCase())} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            O cliente recebe um e-mail pra definir a senha e já começa um trial de 14 dias. Você acompanha o uso por aqui.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-muted/10 sticky bottom-0">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => createMut.mutate()} disabled={!valid || createMut.isPending}>
            {createMut.isPending ? 'Cadastrando...' : 'Cadastrar cliente'}
          </Button>
        </div>
      </div>
    </div>
  );
}
